/**
 * WorkforceWidget — workforce summary (headcount / permanent / contractor /
 * gender / manhours), migrated from the Corporate Safety Dashboard. Reads the
 * existing /dashboard/safety/corporate endpoint (workforce + workforce_breakdown),
 * scoped by the active site. Prefers live HRMS breakdown over the snapshot.
 */
import React from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { CARD, HEADING } from '../ui';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? '0' : Number(n).toLocaleString());

function Tile({ label, value, accent = 'text-gray-900' }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-3 text-center">
      <div className={`text-xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="mt-1 text-xs text-gray-500">{label}</div>
    </div>
  );
}

export default function WorkforceWidget({ title = 'Workforce Summary' }) {
  const { siteId, refreshMs } = useDashboard();
  const { data, loading, error } = useDashboardData(
    (signal) => {
      const p = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
      return axios.get(`${API}/dashboard/safety/corporate${p}`, { signal }).then((r) => r.data);
    },
    [siteId],
    { refreshMs },
  );

  const wb = data?.workforce_breakdown || {};
  const wf = data?.workforce || {};
  const fromHrms = wf.data_source === 'hrms' || wb.total_headcount != null;
  const headcount = wb.total_headcount ?? ((wf.total_employees || 0) + (wf.total_contractors || 0));
  const permanent = wb.permanent_count ?? wf.total_employees;
  const contractor = wb.contractual_count ?? wf.total_contractors;
  const manhours = wf.total_manhours;
  const hasGender = wb.male_count != null || wb.female_count != null;

  return (
    <div className={`${CARD} p-4`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className={`${HEADING} flex items-center gap-2`}>
          <Users className="h-4 w-4 text-gray-400" /> {title}
        </h3>
        {fromHrms && (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">From HRMS</span>
        )}
      </div>

      {loading && <div className="h-24 animate-pulse rounded bg-gray-100" />}
      {!loading && error && <div className="text-sm text-red-600">Unavailable</div>}
      {!loading && !error && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Tile label="Total Headcount" value={fmt(headcount)} accent="text-blue-600" />
          <Tile label="Permanent" value={fmt(permanent)} accent="text-indigo-600" />
          <Tile label="Contractors" value={fmt(contractor)} accent="text-amber-600" />
          <Tile label="Total Manhours" value={fmt(manhours)} accent="text-teal-600" />
          {hasGender && <Tile label="Male" value={fmt(wb.male_count)} accent="text-gray-700" />}
          {hasGender && <Tile label="Female" value={fmt(wb.female_count)} accent="text-gray-700" />}
        </div>
      )}
    </div>
  );
}
